<template>
  <ion-page class="ion-page-white">
    <ion-content>
      <start-header></start-header>
      <div class="p-9 max-w-xl mx-auto" v-if="mfaLoginRequired">
        <h2 class="text-[20px] font-bold py-9">Multi-Factor Authentication</h2>
        <div v-if="mfaLoginContact" class="ion-text-grey mb-6 text-sm">
          We just sent a 6-digit verification code to
          <strong class="ion-text-purple-3 italic">{{ mfaLoginContact }}</strong
          >. Please enter the code to sign in.
        </div>

        <div class="ion-item-wrapper ion-item-wrapper-v7">
          <ion-item class="default" lines="none">
            <ion-input
              label-placement="floating"
              label="Verification Code"
              class="verification-code-input"
              type="tel"
              maxlength="6"
              pattern="[0-9]*"
              inputmode="numeric"
              v-model="codeEntered"
              @input="handleCodeInput"
              :disabled="loading"
              autocomplete="one-time-code"
            ></ion-input>
          </ion-item>
        </div>
        <div
          v-if="verificationMessage"
          class="text-xs my-2 text-center ion-text-green fadein-500"
          v-html="verificationMessage"
        ></div>
        <div
          v-if="verificationError"
          class="text-xs my-2 text-center ion-text-red fadein-500"
          v-html="verificationError"
        ></div>
        <button
          class="button-purple my-8 px-6"
          :disabled="!codeCompleted || verificationError || loading"
          @click="verify"
        >
          <span class="block text-center" v-if="loading">
            <ion-spinner
              name="crescent"
              color="light"
              class="absolute -ml-3 -mt-3"
            ></ion-spinner>
          </span>
          <span class="block capitalize text-white text-lg font-semibold" v-else
            >Next</span
          >
        </button>

        <div class="text-center text-base mb-3">
          <div v-if="resending">
            <ion-spinner
              name="crescent"
              color="primary"
              class="my-1 mx-auto w-[16px]"
            ></ion-spinner>
          </div>
          <div v-else>
            <span class="ion-text-grey text-sm">Didn't receive a code? </span>
            <button fill="clear" @click="resend">
              <span class="ion-text-green font-bold underline">Resend</span>
            </button>
          </div>
        </div>
        <div
          v-if="resendingMessage"
          class="text-xs my-2 text-center ion-text-green fadein-500"
          v-html="resendingMessage"
        ></div>
        <div
          v-if="resendingError"
          class="text-xs my-2 text-center ion-text-red fadein-500"
          v-html="resendingError"
        ></div>

        <button
          fill="clear"
          class="w-full text-center mt-3 mb-6"
          id="open-modal-mfa-need-help"
        >
          <span class="ion-text-grey font-bold underline">Need Help?</span>
        </button>
        <modal-contact-us
          modal-ref="modal-mfa-need-help"
          modal-trigger="open-modal-mfa-need-help"
        >
          <span
            >If you're experiencing issues with MFA, please contact our customer
            service team.</span
          >
        </modal-contact-us>
      </div>
    </ion-content>
  </ion-page>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import {
  useCustomerStore,
  useConfigStore,
  useNotificationsStore,
  useLoansStore,
  useApplicationsStore,
} from "@/stores";
import { storeToRefs } from "pinia";
import { useBrowser } from "@/composables/browser";
import axios from "axios";
import router from "@/router";
import StartHeader from "@/components/start/StartHeader.vue";
import ModalContactUs from "@/components/start/ModalContactUs.vue";
import { useSecureStorage } from "@/composables/secureStorage";
import {
  addCustomerIdToSession,
  AdjustEvents,
  trackEvent,
} from "@/composables/adjust";
const notificationsStore = useNotificationsStore();
const { setNotifications } = notificationsStore;
const customerStore = useCustomerStore();
const configStore = useConfigStore();
const { setConfig } = configStore;
const loansStore = useLoansStore();
const applicationsStore = useApplicationsStore();
const { setLoans } = loansStore;
const { setPendingApplication } = applicationsStore;
const { keys, setStorage, clearAllStorage } = useSecureStorage();
const { urls } = useBrowser();
const {
  email,
  environment,
  authToken,
  deviceID,
  selfBanned,
  passwordResetRequired,
  currentVersion,
  getCustomerParams,
  mfaLoginRequired,
  mfaLoginContact,
  mfaLoginType,
} = storeToRefs(customerStore);
const {
  setSelfBanned,
  setPasswordResetRequired,
  setProfile,
  updateAuth,
  setName,
  setAuthToken,
  setEnvironment,
  getFinanceFundamentals,
} = customerStore;
const loading = ref(false);
const resending = ref(false);
const resendingMessage = ref(null);
const resendingError = ref(null);
const codeEntered = ref("");
const codeCompleted = computed(
  () => String(verificationCode.value).length == 6
);
const verificationCode = computed(() => sanitiseInput(codeEntered.value));
const verificationMessage = ref(null);
const verificationError = ref(null);
const sanitiseInput = (val) => String(val).replace(/\D+/g, "");
const handleCodeInput = () => {
  resending.value = false;
  resendingMessage.value = null;
  verificationError.value = null;
  verificationMessage.value = null;
  setTimeout(() => {
    codeEntered.value = sanitiseInput(codeEntered.value);
  }, 100);
  if (codeEntered.value == "") {
    verificationError.value = null;
  }
};
const init = () => {
  codeEntered.value = "";
  resending.value = false;
  resendingError.value = null;
  verificationError.value = null;
  verificationMessage.value = "";
  if (!mfaLoginRequired.value) {
    router.push("login-email");
  }
};

const headers = (token) => {
  return {
    headers: {
      Accept: "application/json",
      Authorization: "Bearer " + token,
    },
  };
};

const payload = computed(() => ({
  username: email.value,
  code: codeEntered.value,
  device_id: deviceID.value,
}));

const verify = () => {
  loading.value = true;
  axios
    .post(urls.apiMfaVerify, payload.value)
    .then((res) => {
      setAuthToken(res.data.auth_token);
      setEnvironment(res.data.environment);
      updateAuth(true);
      verificationMessage.value = res.data.message;
      setTimeout(() => {
        verificationMessage.value = "";
      }, 3000);
    })
    .then(() => {
      axios
        .get(
          urls.apiCustomer + getCustomerParams.value,
          headers(authToken.value)
        )
        .then((res) => {
          loading.value = false;
          setSelfBanned(res.data.customer.self_banned);
          setPasswordResetRequired(res.data.customer.password_reset_required);
          setProfile(res.data.customer);
          setName(res.data.customer.first_name);
          setConfig(res.data.config);
          setNotifications(res.data.customer.notifications);
          setLoans(res.data.customer.loans);
          setPendingApplication(res.data.customer.pending_application);
          addCustomerIdToSession(res.data.customer.id);
          setStorage({
            key: keys.pro,
            value: JSON.stringify({
              token: authToken.value,
              env: environment.value,
            }),
          });
          setStorage({
            key: keys.name,
            value: res.data.customer.first_name,
          });
          setStorage({ key: keys.attempts, value: "" });
          if (selfBanned.value || passwordResetRequired.value) {
            updateAuth(false);
            clearAllStorage();
            if (selfBanned.value) {
              router.push({ name: "Start" });
            }
            if (passwordResetRequired.value) {
              router.push({ name: "ForgotPassword" });
            }
          } else {
            router.push(Capacitor.isNativePlatform() ? "set-pin" : "home");
          }
        });
    })
    .then(() => {
      getFinanceFundamentals(
        urls.apiFinanceFundamentals +
          "?environment=" +
          environment.value +
          "&app_version=" +
          currentVersion.value,
        authToken.value
      );
    })
    .catch((err) => {
      loading.value = false;
      trackEvent(AdjustEvents.LOGIN_FAILED);
      if (err) {
        verificationError.value = err.response.data.message
          ? err.response.data.message
          : "There was an error processing your request. Please try again later.";
      }
    });
};

const payloadResend = computed(() => ({
  username: email.value,
  mfa_type: mfaLoginType.value,
}));

const resend = () => {
  codeEntered.value = "";
  verificationError.value = null;
  resending.value = true;
  axios
    .post(urls.apiMfaResend, payloadResend.value, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((response) => {
      resending.value = false;
      if (response.status == 200) {
        resendingMessage.value = response.data.message;
        setTimeout(() => {
          resendingMessage.value = "";
        }, 3000);
      }
    })
    .catch((error) => {
      resending.value = false;
      if (error) {
        resendingError.value =
          "There was an error processing your request. Please try again later.";
      }
    });
};

onMounted(() => {
  init();
});
</script>
